/**
 * Phase 68 Plan 03 — usePowerInstabilityCount hook (D-OFF-03 channel 1).
 *
 * Polls `GET /api/v1/admin/power-health/flagged-count` every 30s
 * (mirrors useStaleKiosks cadence). Backend authoritative on which
 * kiosks are flagged via PowerInstabilityAlerter (cluster_acuto OR
 * severity_7d ≥ 10 — see 68-CONTEXT.md D-OFF-02).
 *
 * Returns the typed TanStack `UseQueryResult<FlaggedCountResponse>` so
 * the consumer (PowerInstabilityBadge) gets isLoading + isError + data
 * without extra plumbing.
 *
 * Multi-tenant scoping (Pitfall 8): the qk factory bakes the active
 * circuitId so that on TenantSwitcher toggle the badge re-fetches the
 * count for the new circuit. circuit_admin sees only own-circuit count
 * (backend filter); super_admin sees fleet-wide count.
 *
 * @see Plan 68-02 SUMMARY — `GET /flagged-count` returns
 *      `{count, since_ts, rising_edge}`. The rising_edge field is the
 *      server's view; PowerInstabilityBadge ALSO tracks a previousCount
 *      ref to detect rising-edge client-side (RESEARCH §11.4 — guard
 *      against badge re-mount triggering a stale rising_edge pulse).
 */

import { useQuery } from "@tanstack/react-query";

import { useCircuitId } from "@/hooks/useCircuitId";
import { qk } from "@/lib/queryKeys";
import {
  fetchFlaggedCount,
  type FlaggedCountResponse,
} from "@/api/powerHealth";

export function usePowerInstabilityCount() {
  const circuitId = useCircuitId() ?? "all";
  return useQuery<FlaggedCountResponse>({
    queryKey: qk.powerInstabilityFlagged(circuitId),
    queryFn: fetchFlaggedCount,
    refetchInterval: 30_000,
    staleTime: 25_000,
  });
}
